import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Loader2, Trophy, RefreshCw, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const GRADE_TONE = {
  "A+": "bg-emerald-100 text-emerald-900 border-emerald-200",
  "A": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "B": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "C": "bg-amber-50 text-amber-800 border-amber-200",
  "Risk": "bg-rose-50 text-rose-800 border-rose-200",
};

export default function ManagerScorecards() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachingId, setCoachingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/scorecards/managers?period=month");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, []);

  const sendCoaching = async (m) => {
    if (!window.confirm(`Send a coaching snapshot to ${m.name}? Their latest scorecard + top 2 revenue leaks will be emailed.`)) return;
    setCoachingId(m.userId);
    try {
      const res = await apiClient.post(`/scorecards/managers/${m.userId}/coaching-email`, {});
      if (res.data?.dispatched) {
        toast.success(`Coaching sent to ${res.data.email}`);
      } else {
        toast.error(res.data?.reason || "Coaching not dispatched");
      }
    } catch (e) {
      const body = e?.response?.data;
      const sc = e?.response?.status;
      if (sc === 503) toast.error(body?.reason || "Email service not configured");
      else if (sc === 502) toast.error(`Delivery failed: ${body?.reason || "provider error"}`);
      else toast.error(body?.detail || body?.reason || "Failed to send coaching");
    } finally {
      setCoachingId(null);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card className="p-4 border-slate-200" data-testid="scorecards-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading scorecards…
        </div>
      </Card>
    );
  }
  if (!data || data.forbidden || data.error) return null;

  const rows = (data.managers || []).slice(0, 5);
  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="manager-scorecards">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-600" />
          <h3 className="font-semibold text-slate-900 text-sm">Manager Scorecards</h3>
          <span className="text-xs text-slate-500">this month · top {rows.length}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="scorecards-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="scorecards-empty">
          No management rows yet. Assign direct reports to unlock the scorecard.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="scorecards-list">
          {rows.map((m, i) => (
            <div
              key={m.userId}
              className="flex items-center gap-3 p-2.5 rounded-md bg-slate-50 border border-slate-100"
              data-testid={`scorecard-row-${m.userId}`}
            >
              <span className="text-[10px] font-mono text-slate-400 w-4">#{i + 1}</span>
              <Badge className={`border text-[10px] px-1.5 py-0 ${GRADE_TONE[m.grade] || GRADE_TONE["C"]}`} data-testid={`scorecard-grade-${m.userId}`}>
                {m.grade}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900 truncate font-medium">{m.name}</div>
                <div className="text-[11px] text-slate-500">
                  {m.role} · HC {m.downstreamHeadcount} · {m.teamSignups} signups
                  {m.signupsVsPrevPct !== 0 && (
                    <span className={m.signupsVsPrevPct > 0 ? "text-emerald-600 ml-1" : "text-rose-600 ml-1"}>
                      ({m.signupsVsPrevPct > 0 ? "+" : ""}{m.signupsVsPrevPct}%)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-sm font-semibold text-indigo-700">{fmtGBP(m.valueCapturedGBP)}</div>
                <div className="text-[10px] text-slate-500">{m.score}/100</div>
              </div>
              {(m.grade === "C" || m.grade === "Risk") && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={coachingId === m.userId}
                  onClick={() => sendCoaching(m)}
                  className="h-7 text-[11px] text-indigo-700 border-indigo-200 hover:bg-indigo-50 ml-1"
                  data-testid={`coach-btn-${m.userId}`}
                >
                  {coachingId === m.userId ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <MessageSquare className="h-3 w-3 mr-1" />}
                  Coach
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
